import type { ConnectedPosition } from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, Input, TemplateRef, inject, input } from '@angular/core';

import type { EditorView } from 'prosemirror-view';
import { combineLatest, Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';

import { NgTemplateAssertType, trackByIndex, trackByValue } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type {
  EditorButton,
  EditorButtonRunner,
  EditorButtonsGroup,
} from '@core/prosemirror-legacy';
import { StateUpdated$ } from '@core/prosemirror-legacy/angular';
import {
  ButtonComponent,
  ButtonDropdownIndicatorDirective,
  ConnectedOverlayDirective,
  DropdownComponent,
  DropdownItemComponent,
  DropdownWidth,
  GroupComponent,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
  RtTooltip,
} from '@core/user-interface-legacy';

import { ToolbarService } from './toolbar.service';
import { EditorButtonLabelPipe } from '../../pipes/editor-button-label.pipe';
import { LengthCounterComponent } from '../length-counter/length-counter.component';

@Component({
  selector: 'rt-wysiwyg-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.less'],
  imports: [
    NgTemplateAssertType,
    GroupComponent,
    ButtonComponent,
    IconComponent,
    IconButtonComponent,
    RtTooltip,
    NgTemplateOutlet,
    ButtonDropdownIndicatorDirective,
    LengthCounterComponent,
    EditorButtonLabelPipe,
    ConnectedOverlayDirective,
    OverlayOriginDirective,
    DropdownComponent,
    DropdownItemComponent,
    TranslatePipe,
  ],
})
export class PmToolbarComponent {
  private readonly toolbarService = inject(ToolbarService);
  private readonly stateUpdated$ = inject(StateUpdated$);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly i18nService = inject(I18nService);

  protected readonly DropdownWidth = DropdownWidth;
  protected readonly moreMenuPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
    },
  ];

  public readonly editorView = input<EditorView>(undefined!);
  public readonly editorHasFocus = input<boolean>(undefined!);
  @Input() public buttonWrapperTemplate: TemplateRef<any>;
  public disabled: boolean;
  public ButtonTemplateType: { groups: EditorButtonsGroup[]; state: boolean[] };
  public ButtonContentType: { button: EditorButton };

  public leftOutlet: EditorButtonsGroup[] = [];
  public rightOutlet: EditorButton[] = [];
  public moreOutlet: EditorButton[] = [];
  public leftOutletState: boolean[] = [];
  public rightOutletState: boolean[] = [];
  public moreOutletState: boolean[] = [];
  public tooltips: Record<string, string> = {};
  public trackByValue = trackByValue;
  public trackByIndex = trackByIndex;

  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit() {
    const { leftOutletButtons$, rightOutletButtons$, moreOutletButtons$ } = this.toolbarService;
    combineLatest([
      leftOutletButtons$,
      rightOutletButtons$,
      moreOutletButtons$,
      this.stateUpdated$.pipe(startWith(null)),
    ])
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(([left, right, more]) => {
        const { state } = this.editorView();
        this.disabled = !this.editorView().editable;
        if (left !== this.leftOutlet || right !== this.rightOutlet || more !== this.moreOutlet) {
          this.tooltips = this.translateTooltips([...left.flatMap(val => val), ...right, ...more]);
          this.leftOutlet = left;
          this.rightOutlet = right;
          this.moreOutlet = more;
        }
        this.leftOutletState = left.flatMap(button => button).map(button => button.isActive(state));
        this.rightOutletState = right.map(button => button.isActive(state));
        this.moreOutletState = more.map(button => button.isActive(state));
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public onButtonClick(item: { run: EditorButtonRunner }, origin: HTMLElement) {
    const { state, dispatch } = this.editorView();
    item.run(state, dispatch, origin, false);
  }

  private translateTooltips(buttons: EditorButton[]) {
    return Object.fromEntries(
      buttons.map(button => {
        if (!button.tooltipText) return [];
        const text = `${this.i18nService.translate(button.tooltipText)} ${
          button.tooltipShortcut || ''
        }`.trim();
        return [button.tooltipText, text];
      }),
    );
  }
}
